<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div ref="containerEl" class="scratchoff">
    <div class="scratchoff__content">
      <div v-if="fields.titleLabel?.key" class="scratchoff__title">
        <TipTap v-model="titleValue" mode="single" />
      </div>
      <div class="scratchoff__inner">
        <!-- SVG dashed frame — CSS `border: dashed` produces tiny
             near-dot segments; inline SVG with an explicit
             stroke-dasharray gives the exact 8-on/8-off pattern the
             design uses. No viewBox means user units = CSS px. -->
        <svg class="scratchoff__inner-border" width="100%" height="100%" aria-hidden="true">
          <rect
            class="scratchoff__inner-border-rect"
            fill="none"
            stroke-width="1"
            stroke-dasharray="8 8"
            rx="12"
            ry="12"
          />
        </svg>
        <div class="scratchoff__inner-content">
          <div v-if="fields.percentLabel?.key" class="scratchoff__percent-box">
            <TipTap v-model="percentValue" mode="single" />
          </div>
          <div v-if="fields.subtitleLabel?.key" class="scratchoff__subtitle">
            <TipTap v-model="subtitleValue" />
          </div>
          <div v-if="fields.footnoteLabel?.key" class="scratchoff__footnote">
            <TipTap v-model="footnoteValue" mode="single" />
          </div>
        </div>
      </div>
    </div>
    <canvas
      v-show="maskVisible"
      ref="maskCanvasEl"
      class="scratchoff__mask"
      :class="{ 'is-fading': isFading }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @pointerleave="onPointerUp"
    />
    <svg
      v-if="maskVisible && !isFading && showHandHint"
      class="scratchoff__hand"
      :width="handSizePx"
      :height="handHeightPx"
      viewBox="106.63 58.58 53.15 58.69"
      preserveAspectRatio="xMidYMin meet"
      :style="handStyle"
      aria-hidden="true"
    >
      <path :d="HAND_OUTLINE_D" fill="black" />
      <path :d="HAND_FILL_D" fill="white" />
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import type { ColorField, SizeField } from '@render/types/styles'

import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import { useTranslation } from '@render/composables/useTranslation'
import { useComponentProcessors } from '@render/composables/useComponentProcessors'
import { useEditorCommands } from '@render/composables/useEditorCommands'
import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import TipTap from '@render/lib/TipTap'
import type { ScratchoffFields as Model } from './definition'

const fields = defineModel<Model>('fields', {
  required: true,
  default: (): Model => ({}),
})

const emit = defineEmits<{
  (e: 'onReveal'): void
}>()

// Hand sprite — path data lifted directly from scratchoff.cursor.overlay.svg.
// Path-bounds in the original overlay coords:
//   x ∈ [106.632, 159.775]  (width 53.143)
//   y ∈ [58.5833, 117.267]  (height 58.684)
// The fingertip — the point that needs to align with the scratch path —
// is the topmost point of the outline at (131.425, 58.5833).
// The viewBox `106.63 58.58 53.15 58.69` starts the viewport EXACTLY at
// the fingertip's y (not a few pixels above). Combined with
// `preserveAspectRatio="xMidYMin meet"`, any aspect-ratio padding falls
// at the BOTTOM of the element instead of being split between top/bottom,
// so the fingertip stays pinned at element local y = 0 regardless of
// sprite size.
const HAND_FILL_D =
  'M129.867 108.75C130.575 110.025 132.558 112.008 133.408 112.717C133.68 112.989 133.833 113.358 133.833 113.743V114.575C133.833 114.957 134.143 115.267 134.525 115.267H152.048C152.472 115.267 152.817 114.922 152.817 114.498V113.283C152.817 112.837 153.027 112.417 153.383 112.15C156.217 109.742 157.775 106.342 157.775 102.658V81.9749C157.775 80.5583 156.642 79.4249 155.225 79.4249C153.808 79.4249 152.817 80.4166 152.675 81.6916V88.6333C152.675 89.4833 152.108 90.0499 151.258 90.0499C150.408 90.0499 149.842 89.4833 149.842 88.6333V76.8749C149.842 75.4583 148.708 74.3249 147.292 74.3249C145.875 74.3249 144.883 75.3166 144.742 76.5916V87.7833C144.742 88.6333 144.175 89.1999 143.325 89.1999C142.475 89.1999 141.908 88.6333 141.908 87.7833V75.3166C141.908 73.8999 140.775 72.7666 139.358 72.7666C137.942 72.7666 136.95 73.7583 136.808 75.0333V75.3166V87.7833C136.808 88.6333 136.242 89.1999 135.392 89.1999C134.542 89.1999 133.975 88.6333 133.975 87.7833V63.1333C133.975 61.7166 132.842 60.5833 131.425 60.5833C130.008 60.5833 128.875 61.7166 128.875 63.1333V96.7083L118.533 91.0416C115.983 89.4833 112.158 89.1999 109.75 91.4666L124.767 102.517C126.75 104.358 128.592 106.483 129.867 108.75Z'
const HAND_OUTLINE_D =
  'M152.816 114.497V113.283C152.816 112.893 152.978 112.523 153.257 112.257L153.384 112.15C156.128 109.817 157.676 106.553 157.771 103.003L157.775 102.658V81.9749C157.775 80.6468 156.779 79.5675 155.487 79.4377L155.225 79.425C153.808 79.4252 152.816 80.4168 152.675 81.6917V88.6331C152.675 89.4829 152.109 90.0498 151.259 90.05L151.103 90.0432C150.391 89.9823 149.91 89.5011 149.849 88.7893L149.842 88.6331V76.8752C149.842 75.5471 148.846 74.4678 147.554 74.3381L147.292 74.3245C145.875 74.3245 144.883 75.317 144.741 76.592V87.7834L144.735 87.9397C144.67 88.702 144.122 89.1994 143.325 89.1995L143.169 89.1936C142.457 89.1326 141.976 88.6512 141.915 87.9397L141.908 87.7834V75.3167C141.908 73.9884 140.912 72.9091 139.62 72.7795L139.358 72.7668C137.942 72.7668 136.95 73.7584 136.809 75.0334V87.7834L136.802 87.9397C136.736 88.7021 136.188 89.1995 135.392 89.1995C134.595 89.1994 134.047 88.7021 133.981 87.9397L133.975 87.7834V63.1331C133.975 61.7165 132.841 60.5833 131.425 60.5833C130.008 60.5834 128.875 61.7165 128.875 63.1331V96.7083L118.533 91.0413C115.983 89.4831 112.158 89.2004 109.75 91.467L106.632 91.6555L123.482 104.054C125.338 105.787 126.996 107.727 128.123 109.73C128.603 110.589 129.415 111.548 130.119 112.307C130.719 112.953 131.347 113.562 131.833 113.994V114.574C131.833 116.061 133.039 117.267 134.525 117.267H152.048C153.577 117.267 154.816 116.027 154.816 114.497V113.553C158.011 110.77 159.775 106.866 159.775 102.658V81.9749C159.775 79.4536 157.746 77.425 155.225 77.425C153.832 77.4252 152.653 77.9933 151.842 78.887V76.8752C151.842 74.3541 149.813 72.3246 147.292 72.3245C145.786 72.3245 144.529 72.9891 143.718 74.011C143.159 72.1306 141.425 70.7669 139.358 70.7668C137.965 70.7668 136.786 71.3348 135.975 72.2288V63.1331C135.975 60.6119 133.946 58.5833 131.425 58.5833C128.904 58.5834 126.875 60.612 126.875 63.1331V93.3313L119.505 89.2932C116.46 87.4699 111.629 86.9509 108.379 90.01L106.632 91.6555L123.482 104.054C125.338 105.787 126.996 107.727 128.123 109.73C128.603 110.589 129.415 111.548 130.119 112.307C130.719 112.953 131.347 113.562 131.833 113.994V114.574C131.833 116.061 133.039 117.267 134.525 117.267H152.048C153.577 117.267 154.816 116.027 154.816 114.497Z'

// Finger tip normalized offsets relative to the hand's rendered bounding
// box (viewBox). Tip X = (131.425 - 106.63) / 53.15 = 0.467.
// Tip Y = 0 (viewBox now starts at the fingertip).
const HAND_TIP_NX = 0.467
const HAND_TIP_NY = 0
// Aspect ratio of the tight path bounding box (height / width).
const HAND_ASPECT = 58.69 / 53.15

const titleValue = useTranslation(() => fields.value.titleLabel?.key)
const percentValue = useTranslation(() => fields.value.percentLabel?.key)
const subtitleValue = useTranslation(() => fields.value.subtitleLabel?.key)
const footnoteValue = useTranslation(() => fields.value.footnoteLabel?.key)
const pipe = usePipe()

// ---------- Mode / visibility ----------
const isEditor = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)

// In non-editor modes the mask is always shown (until revealed). In editor
// mode the mask is hidden by default so authors can see/edit the underlying
// text; it's revealed only while a preview command is active.
const editorMaskVisible = ref(false)
const isRevealed = ref(false)
const isFading = ref(false)
const maskVisible = computed(() => {
  if (isRevealed.value) return false
  return isEditor.value ? editorMaskVisible.value : true
})

// The hand hint runs in non-editor modes or during a preview-animation run.
// `handHintEnabled === false` disables it; `undefined` treats it as enabled
// (defaults live in the component, not the definition).
const handHintActive = ref(false)
const showHandHint = computed(() => {
  if (fields.value.handHintEnabled === false) return false
  if (userHasInteracted.value) return false
  return !isEditor.value || handHintActive.value
})

// ---------- Canvas / mask ----------
const containerEl = ref<HTMLDivElement | null>(null)
const maskCanvasEl = ref<HTMLCanvasElement | null>(null)
let maskCtx: CanvasRenderingContext2D | null = null
let resizeObserver: ResizeObserver | null = null

const userHasInteracted = ref(false)
const handPos = ref({ x: 0, y: 0 })

const maskColorStr = computed(
  () => getColorStyleData(fields.value.maskColor, '#BDE5DF') ?? '#BDE5DF',
)
const handTrailColorStr = computed(
  () => getColorStyleData(fields.value.handTrailColor, '#FFFFFF') ?? '#FFFFFF',
)
const brushRadius = computed(() => Math.max(4, Number(fields.value.brushSize ?? 45)))
const handSizePx = computed(() => Math.max(20, Number(fields.value.handSize ?? 80)))
const handHeightPx = computed(() => Math.round(handSizePx.value * HAND_ASPECT))

function fillMask() {
  const canvas = maskCanvasEl.value
  const ctx = maskCtx
  if (!canvas || !ctx) return
  ctx.globalCompositeOperation = 'source-over'
  // Canvas needs a readable color string — resolve `var(--x)` against
  // the canvas element so we're not writing `rgba(var(--…))` literals.
  ctx.fillStyle = resolveCssColor(maskColorStr.value, canvas)
  ctx.fillRect(0, 0, canvas.width, canvas.height)
}

function resolveCssColor(value: string, el: HTMLElement): string {
  if (!value.startsWith('var(')) return value
  const probe = document.createElement('span')
  probe.style.color = value
  probe.style.display = 'none'
  el.appendChild(probe)
  const resolved = getComputedStyle(probe).color
  probe.remove()
  return resolved || '#BDE5DF'
}

function eraseAt(x: number, y: number, radius: number) {
  const ctx = maskCtx
  if (!ctx) return
  ctx.globalCompositeOperation = 'destination-out'
  ctx.beginPath()
  ctx.arc(x, y, radius, 0, Math.PI * 2)
  ctx.fill()
}

function resizeCanvasToContainer() {
  const canvas = maskCanvasEl.value
  if (!canvas) return
  const dpr = window.devicePixelRatio || 1
  // Size the buffer to the canvas's CONTENT area (inside its own border),
  // not the outer `.scratchoff` rect. This keeps the buffer ↔ display
  // scale at exactly 1/dpr, so buffer coords map to content-area CSS
  // coords with no fractional scaling — which is what the hand dot's
  // coordinate conversion assumes.
  const w = Math.max(1, Math.round(canvas.clientWidth * dpr))
  const h = Math.max(1, Math.round(canvas.clientHeight * dpr))
  if (canvas.width === w && canvas.height === h) return
  canvas.width = w
  canvas.height = h
  maskCtx = canvas.getContext('2d')
  if (maskCtx) {
    maskCtx.scale(1, 1)
    fillMask()
  }
}

function pointToCanvas(e: PointerEvent): [number, number] | null {
  const canvas = maskCanvasEl.value
  if (!canvas) return null
  const rect = canvas.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return null
  const x = ((e.clientX - rect.left) / rect.width) * canvas.width
  const y = ((e.clientY - rect.top) / rect.height) * canvas.height
  return [x, y]
}

let pointerDown = false
function onPointerDown(e: PointerEvent) {
  if (isRevealed.value || isFading.value) return
  pointerDown = true
  const isFirstInteraction = !userHasInteracted.value
  userHasInteracted.value = true
  stopHandHint()
  // Hand-hint paints a trail on top of the mask (source-over). Wipe it
  // before the first erase so the user's scratch starts on a clean mask.
  if (isFirstInteraction) fillMask()
  maskCanvasEl.value?.setPointerCapture(e.pointerId)
  const p = pointToCanvas(e)
  if (!p) return
  // Scale brush to canvas-pixel space (canvas is DPR-upscaled).
  eraseAt(p[0], p[1], brushRadius.value * (window.devicePixelRatio || 1))
  if (fields.value.revealOnRelease === false) scheduleRevealCheck()
}

let lastErase: [number, number] | null = null
function onPointerMove(e: PointerEvent) {
  if (!pointerDown || isRevealed.value || isFading.value) return
  const p = pointToCanvas(e)
  if (!p) return
  const ctx = maskCtx
  if (!ctx) return
  const dpr = window.devicePixelRatio || 1
  const r = brushRadius.value * dpr
  // Connect consecutive pointer samples with a thick line so fast swipes
  // don't leave gaps between discrete arcs.
  if (lastErase) {
    ctx.globalCompositeOperation = 'destination-out'
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.lineWidth = r * 2
    ctx.beginPath()
    ctx.moveTo(lastErase[0], lastErase[1])
    ctx.lineTo(p[0], p[1])
    ctx.stroke()
  }
  eraseAt(p[0], p[1], r)
  lastErase = p
  if (fields.value.revealOnRelease === false) scheduleRevealCheck()
}

function onPointerUp() {
  const wasDown = pointerDown
  pointerDown = false
  lastErase = null
  if (wasDown && fields.value.revealOnRelease !== false) scheduleRevealCheck()
}

// ---------- Reveal threshold sampling ----------
let revealCheckScheduled = false
function scheduleRevealCheck() {
  if (revealCheckScheduled) return
  revealCheckScheduled = true
  requestAnimationFrame(() => {
    revealCheckScheduled = false
    if (isRevealed.value) return
    const pct = sampleRevealedPercent()
    const threshold = Math.max(0, Number(fields.value.revealThreshold ?? 30))
    if (pct >= threshold) triggerReveal()
  })
}

function sampleRevealedPercent(): number {
  const canvas = maskCanvasEl.value
  const ctx = maskCtx
  if (!canvas || !ctx) return 0
  // Sample on a small grid — reading the full canvas every frame is costly.
  const cols = 40
  const rows = 40
  const stepX = Math.max(1, Math.floor(canvas.width / cols))
  const stepY = Math.max(1, Math.floor(canvas.height / rows))
  try {
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data
    let transparent = 0
    let total = 0
    for (let y = 0; y < canvas.height; y += stepY) {
      for (let x = 0; x < canvas.width; x += stepX) {
        const idx = (y * canvas.width + x) * 4 + 3
        if ((data[idx] ?? 255) < 32) transparent++
        total++
      }
    }
    return total === 0 ? 0 : (transparent / total) * 100
  } catch {
    return 0
  }
}

// ---------- Reveal / reset ----------
let autoRevealTimer: ReturnType<typeof setTimeout> | null = null
let fadeTimer: ReturnType<typeof setTimeout> | null = null

function triggerReveal() {
  if (isRevealed.value || isFading.value) return
  stopHandHint()
  if (autoRevealTimer) {
    clearTimeout(autoRevealTimer)
    autoRevealTimer = null
  }
  isFading.value = true
  const duration = Math.max(0, Number(fields.value.autoRevealFadeDuration ?? 600))
  fadeTimer = setTimeout(() => {
    isRevealed.value = true
    isFading.value = false
    fadeTimer = null
    if (!isEditor.value) emit('onReveal')
  }, duration)
}

function resetMask() {
  if (fadeTimer) {
    clearTimeout(fadeTimer)
    fadeTimer = null
  }
  if (autoRevealTimer) {
    clearTimeout(autoRevealTimer)
    autoRevealTimer = null
  }
  isFading.value = false
  isRevealed.value = false
  userHasInteracted.value = false
  lastErase = null
  fillMask()
  scheduleAutoRevealTimer()
  if (!isEditor.value && fields.value.handHintEnabled !== false) startHandHint()
}

function scheduleAutoRevealTimer() {
  if (autoRevealTimer) {
    clearTimeout(autoRevealTimer)
    autoRevealTimer = null
  }
  const t = Number(fields.value.autoRevealTimeout ?? 0)
  if (!t || t <= 0) return
  if (isEditor.value) return
  autoRevealTimer = setTimeout(() => {
    autoRevealTimer = null
    triggerReveal()
  }, t)
}

// ---------- Hand hint loop ----------
//
// One shared parametric path drives both the mask-erase brush and the hand
// sprite. `t` advances linearly from 0..1 over `handAnimationDuration` — so
// both follow identical (x, y) coordinates at identical speed. The path is
// a vertical sine-zig-zag: y sweeps top-to-bottom, x oscillates around
// center with `HINT_WAVE_CYCLES` peaks.
const HINT_PAD_Y = 0.15 // fraction of canvas height kept clear at top/bottom
const HINT_PAD_X = 0.25 // fraction of canvas width kept clear at left/right
const HINT_WAVE_CYCLES = 3 // number of full sine cycles across the sweep

function hintPathAt(t: number, w: number, h: number): { x: number; y: number } {
  const ampX = (w - w * HINT_PAD_X * 2) / 2
  const sweepY = h - h * HINT_PAD_Y * 2
  return {
    x: w / 2 + Math.sin(t * Math.PI * 2 * HINT_WAVE_CYCLES) * ampX,
    y: h * HINT_PAD_Y + t * sweepY,
  }
}

let hintRaf: number | null = null
let hintStart = 0
let hintLoopIndex = 0
// Previous path point (buffer coords) — used to stroke a line segment
// between successive frames so the hint line appears progressively
// along the path as the hand moves.
let lastHintBufX: number | null = null
let lastHintBufY: number | null = null

function startHandHint() {
  if (hintRaf !== null) return
  hintStart = performance.now()
  hintLoopIndex = -1
  handHintActive.value = true
  lastHintBufX = null
  lastHintBufY = null
  // Reset so the hand starts on a fresh mask.
  fillMask()
  hintRaf = requestAnimationFrame(handHintFrame)
}

function stopHandHint() {
  handHintActive.value = false
  if (hintRaf !== null) {
    cancelAnimationFrame(hintRaf)
    hintRaf = null
  }
  lastHintBufX = null
  lastHintBufY = null
}

function handHintFrame(ts: number) {
  hintRaf = null
  if (!maskCanvasEl.value || userHasInteracted.value) return
  const duration = Math.max(400, Number(fields.value.handAnimationDuration ?? 3000))
  const elapsed = ts - hintStart
  const t = (elapsed % duration) / duration
  const currentLoop = Math.floor(elapsed / duration)
  // New loop: refill the mask so the next sweep starts fresh.
  if (currentLoop !== hintLoopIndex) {
    hintLoopIndex = currentLoop
    fillMask()
    lastHintBufX = null
    lastHintBufY = null
  }

  const canvas = maskCanvasEl.value
  const w = canvas.width
  const h = canvas.height
  const { x, y } = hintPathAt(t, w, h)

  // Paint a thin line on TOP of the mask (source-over, not destination-out)
  // — the hint animation never reveals the underlying content, it just
  // draws the hand's trail visually over the mask. Each frame extends the
  // line from the previous path point to the current one.
  const ctx = maskCtx
  if (ctx && lastHintBufX !== null && lastHintBufY !== null) {
    const dpr = window.devicePixelRatio || 1
    ctx.globalCompositeOperation = 'source-over'
    ctx.strokeStyle = resolveCssColor(handTrailColorStr.value, canvas)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.lineWidth = brushRadius.value * dpr
    ctx.beginPath()
    ctx.moveTo(lastHintBufX, lastHintBufY)
    ctx.lineTo(x, y)
    ctx.stroke()
  }
  lastHintBufX = x
  lastHintBufY = y

  // Convert buffer coords to `.scratchoff` padding-box coords using the
  // canvas's CONTENT area (same rectangle the line above renders into).
  // `clientLeft` / `clientTop` are the canvas's own border widths — the
  // buffer's (0, 0) renders at pbox (clientLeft, clientTop), not (0, 0).
  // Without this offset the dot drifts `borderWidth` px from the line at
  // the edges of the sweep.
  const cssX = canvas.clientLeft + (x / w) * canvas.clientWidth
  const cssY = canvas.clientTop + (y / h) * canvas.clientHeight
  handPos.value = { x: cssX, y: cssY }

  hintRaf = requestAnimationFrame(handHintFrame)
}

const handStyle = computed(() => {
  const tipX = handSizePx.value * HAND_TIP_NX
  const tipY = handHeightPx.value * HAND_TIP_NY
  return {
    transform: `translate(${handPos.value.x - tipX}px, ${handPos.value.y - tipY}px)`,
  }
})

// ---------- Editor preview commands ----------
let previewMaskTimer: ReturnType<typeof setTimeout> | null = null

function previewAnimation() {
  if (!isEditor.value) return
  if (previewMaskTimer) {
    clearTimeout(previewMaskTimer)
    previewMaskTimer = null
  }
  userHasInteracted.value = false
  isFading.value = false
  isRevealed.value = false
  editorMaskVisible.value = true
  // Wait a tick so the canvas v-show flip lands and the element has a size
  // before we size+refill + start the rAF loop.
  requestAnimationFrame(() => {
    resizeCanvasToContainer()
    startHandHint()
  })
  const duration = Math.max(1000, Number(fields.value.handAnimationDuration ?? 3000))
  // Run ~2 full loops so the author can see the animation clearly.
  previewMaskTimer = setTimeout(() => {
    stopHandHint()
    editorMaskVisible.value = false
    previewMaskTimer = null
  }, duration * 2)
}

function previewMask() {
  if (!isEditor.value) return
  if (previewMaskTimer) {
    clearTimeout(previewMaskTimer)
    previewMaskTimer = null
  }
  stopHandHint()
  userHasInteracted.value = false
  isFading.value = false
  isRevealed.value = false
  editorMaskVisible.value = true
  // `resizeCanvasToContainer` only repaints when the buffer dimensions
  // change. After a prior `previewAnimation` run the canvas still holds
  // the zigzag hint trail, so we force a `fillMask()` to wipe it — the
  // preview-mask command should show a *clean* mask only.
  requestAnimationFrame(() => {
    resizeCanvasToContainer()
    fillMask()
  })
  previewMaskTimer = setTimeout(() => {
    editorMaskVisible.value = false
    previewMaskTimer = null
  }, 7000)
}

// ---------- Lifecycle / wiring ----------
onMounted(() => {
  resizeCanvasToContainer()
  if (containerEl.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      // Resize only while the mask is present — otherwise we'd lose any
      // scratch progress on every parent layout change.
      if (!userHasInteracted.value && !isRevealed.value) {
        resizeCanvasToContainer()
      }
    })
    resizeObserver.observe(containerEl.value)
  }
  if (!isEditor.value) {
    scheduleAutoRevealTimer()
    if (fields.value.handHintEnabled !== false) startHandHint()
  }
})

onBeforeUnmount(() => {
  stopHandHint()
  if (autoRevealTimer) clearTimeout(autoRevealTimer)
  if (fadeTimer) clearTimeout(fadeTimer)
  if (previewMaskTimer) clearTimeout(previewMaskTimer)
  resizeObserver?.disconnect()
  resizeObserver = null
})

// Repaint when mask color changes (editor live-edit).
watch(maskColorStr, () => {
  if (!userHasInteracted.value && !isRevealed.value) fillMask()
})

useComponentProcessors({
  reset: () => resetMask(),
  reveal: () => triggerReveal(),
})

useEditorCommands({
  previewAnimation: () => previewAnimation(),
  previewMask: () => previewMask(),
})

// ---------- Styling ----------
function col(f: ColorField | undefined, fallback: string) {
  return getColorStyleData(f) || fallback
}
function sz(f: SizeField | undefined, fallback: string) {
  return getSizeStyleData(f) || fallback
}

const css = computed(() => {
  const bgStart = col(fields.value.contentBgColor, '#DAF1ED')
  const bgMid = col(fields.value.contentBgColorMid, '#FFFFFF')
  const borderStart = col(fields.value.borderColor, '#29ABE2')
  const borderEnd = col(fields.value.borderColorEnd, '#03CEA4')
  const percentStart = col(fields.value.percentBoxBgColor, '#28ABE2')
  const percentEnd = col(fields.value.percentBoxBgColorEnd, '#03CDA4')
  const percentTextStart = col(fields.value.percentColor, '#E1FFF8')
  const percentTextEnd = col(fields.value.percentColorEnd, '#F6FBFE')
  return {
    width: sz(fields.value.width, '242px'),
    height: sz(fields.value.height, '346px'),
    padding: getSpacingStyleData(fields.value.padding, '24px'),
    margin: getSpacingStyleData(fields.value.margin, '0'),
    borderWidth: sz(fields.value.borderWidth, '2px'),
    borderRadius: sz(fields.value.borderRadius, '16px'),
    titleColor: col(fields.value.titleColor, '#587470'),
    titleFontSize: sz(fields.value.titleFontSize, '16px'),
    percentFontSize: sz(fields.value.percentFontSize, '48px'),
    subtitleColor: col(fields.value.subtitleColor, '#0B3E53'),
    subtitleFontSize: sz(fields.value.subtitleFontSize, '16px'),
    footnoteColor: col(fields.value.footnoteColor, '#0B3E53'),
    footnoteFontSize: sz(fields.value.footnoteFontSize, '14px'),
    innerBoxBg: col(fields.value.innerBoxBgColor, '#FFFFFF'),
    innerBoxBorderColor: col(fields.value.innerBoxBorderColor, '#85A29E'),
    // Three-stop diagonal matching `paint0_linear_5215_19363` (start → mid → start).
    contentBgGradient: `linear-gradient(135deg, ${bgStart} 0%, ${bgMid} 50%, ${bgStart} 100%)`,
    // Gradient border rendered via background-clip: border-box + transparent
    // border. Two-color simplification of the SVG's 4-stop paint1.
    borderGradient: `linear-gradient(135deg, ${borderStart} 0%, ${borderEnd} 100%)`,
    // `paint2_linear_5215_19363` — bottom-left to top-right (~60deg).
    percentBoxGradient: `linear-gradient(60deg, ${percentStart} 0%, ${percentEnd} 100%)`,
    // `paint3_linear_5215_19363` — top → bottom, clipped to the discount
    // text glyphs below. Both stops configurable via `percentColor` /
    // `percentColorEnd`.
    percentTextGradient: `linear-gradient(180deg, ${percentTextStart} 0%, ${percentTextEnd} 100%)`,
    maskBorderColor: col(fields.value.maskBorderColor, '#85A29E'),
    maskBorderStyle: fields.value.maskBorderEnabled === false ? 'none' : 'dashed',
    fadeDuration: `${Math.max(0, Number(fields.value.autoRevealFadeDuration ?? 600))}ms`,
    boxShadow:
      fields.value.shadowEnabled === false
        ? 'none'
        : `6px 12px 24px ${col(fields.value.shadowColor, 'rgba(11, 46, 59, 0.16)')}`,
  }
})
</script>

<style scoped lang="scss">
.scratchoff {
  position: relative;
  width: v-bind('css.width');
  height: v-bind('css.height');
  margin: v-bind('css.margin');
  // Gradient border + gradient fill via double-background with different
  // `background-clip` origins. `padding-box` fills only the area inside
  // the border; `border-box` fills the entire element. With a transparent
  // border the outer gradient shows through at the border width, giving
  // a gradient border that still respects `border-radius`.
  background:
    v-bind('css.contentBgGradient') padding-box,
    v-bind('css.borderGradient') border-box;
  border: v-bind('css.borderWidth') solid transparent;
  border-radius: v-bind('css.borderRadius');
  box-shadow: v-bind('css.boxShadow');
  box-sizing: border-box;
  overflow: hidden;
  user-select: none;
}

.scratchoff__content {
  position: absolute;
  inset: 0;
  padding: v-bind('css.padding');
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  box-sizing: border-box;
}

// Typography values map to design specs (Avenir "Body Bold" / "Heading 1" /
// "Small Body Regular"). Sizes are overridable via field; family, weight,
// line-height and font-feature-settings are hardcoded to match the design.
.scratchoff__title {
  width: 100%;
  color: v-bind('css.titleColor');
  font-family: Avenir, sans-serif;
  font-size: v-bind('css.titleFontSize');
  font-style: normal;
  font-weight: 900;
  line-height: 24px;
  text-align: center;
  font-feature-settings:
    'liga' off,
    'clig' off;
  margin-bottom: 12px;
}

.scratchoff__inner {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  background-color: v-bind('css.innerBoxBg');
  border-radius: 12px;
  box-sizing: border-box;
}

// Absolute-positioned SVG that draws the dashed frame. No CSS border on
// the parent — `border: dashed` in CSS produces near-dot-length segments
// instead of the 8×8 pattern the design calls for.
.scratchoff__inner-border {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
}

// Rect attributes set via CSS so they track the parent size without
// needing JS. `x: 0.5px` / `width: calc(100% - 1px)` keeps the 1px
// stroke fully inside the SVG so it isn't clipped at the edges.
.scratchoff__inner-border-rect {
  x: 0.5px;
  y: 0.5px;
  width: calc(100% - 1px);
  height: calc(100% - 1px);
  stroke: v-bind('css.innerBoxBorderColor');
}

.scratchoff__inner-content {
  position: absolute;
  inset: 0;
  // Top padding (16) + discount box inner padding (16) = 32 px from the
  // dashed-rect top to the discount TEXT, per spec. Horizontal padding
  // (8) places subtitle/footnote 8 px in from the dashed sides; the
  // discount box adds its own margin for a 16 px inset. Bottom 8 keeps
  // the footnote 8 px above the dashed bottom.
  padding: 16px 8px 8px;
  display: flex;
  flex-direction: column;
  // align-items defaults to `stretch` so subtitle/footnote span the full
  // content width and wrap naturally. The discount box opts out with
  // `align-self: center` below to shrink-wrap its content.
  text-align: center;
  box-sizing: border-box;
}

.scratchoff__percent-box {
  // Shrink-wrap around the discount text with 16 px padding all around.
  align-self: center;
  // Cap at dashed-rect width minus the 16-px inset on each side
  // (parent's 8 px + this 8 px margin each side = 16 px).
  max-width: calc(100% - 16px);
  margin: 0 8px 8px;
  padding: 16px;
  background: v-bind('css.percentBoxGradient');
  border-radius: 12px;
  box-sizing: border-box;

  font-family: Avenir, sans-serif;
  font-size: v-bind('css.percentFontSize');
  font-style: normal;
  font-weight: 900;
  line-height: 56px;
  text-align: center;
  font-feature-settings:
    'liga' off,
    'clig' off;

  // Discount TEXT uses a top→bottom gradient fill clipped to the glyphs
  // (SVG `paint3_linear_5215_19363`). `background-clip: text` +
  // transparent text-fill paints the gradient inside the letter shapes
  // against the box's own gradient background. Stops configurable via
  // `percentColor` / `percentColorEnd` fields.
  :deep(*) {
    background: v-bind('css.percentTextGradient');
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
}

.scratchoff__subtitle {
  width: 100%;
  color: v-bind('css.subtitleColor');
  font-family: Avenir, sans-serif;
  font-size: v-bind('css.subtitleFontSize');
  font-style: normal;
  font-weight: 900;
  line-height: 24px;
  text-align: center;
  font-feature-settings:
    'liga' off,
    'clig' off;
  // Wrap long content rather than overflowing the dashed box.
  overflow-wrap: break-word;
  word-break: break-word;
}

.scratchoff__footnote {
  width: 100%;
  color: v-bind('css.footnoteColor');
  font-family: Avenir, sans-serif;
  font-size: v-bind('css.footnoteFontSize');
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
  text-align: center;
  font-feature-settings:
    'liga' off,
    'clig' off;
  // `margin-top: auto` pins the footnote to the bottom of the flex
  // column regardless of subtitle's height, so it always sits 8 px
  // above the dashed-rect bottom (the parent's bottom padding).
  margin-top: auto;
  overflow-wrap: break-word;
  word-break: break-word;
}

.scratchoff__mask {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  touch-action: none;
  cursor: crosshair;
  border-radius: inherit;
  border-style: v-bind('css.maskBorderStyle');
  border-color: v-bind('css.maskBorderColor');
  border-width: 1px;
  box-sizing: border-box;
  opacity: 1;
  transition: opacity v-bind('css.fadeDuration') ease;

  &.is-fading {
    opacity: 0;
    pointer-events: none;
  }
}

.scratchoff__hand {
  position: absolute;
  left: 0;
  top: 0;
  display: block;
  pointer-events: none;
  will-change: transform;
  filter: drop-shadow(0 2px 3px rgb(0 0 0 / 0.25));
}
</style>
